import {atom, useRecoilState, useRecoilValue} from "recoil";
import React, {FC, useEffect} from "react";
import {useAppInitTradeInAppQuery, WholeSaleCategory} from "../../Queries";
import {AccessContextData} from "./AppContextProvider";
import {Images} from "../../Images";
import {Analytics} from "../../analytics/Analytics";
import {ImageServerUrl} from "../../Settings";

export interface Localization {
   language: string
   key: string
   value: string
}

export interface AppData {
   initialized: boolean
   localizations: Localization[]
   wholeSaleActive: boolean
   invalidEmailReg?: string
   maximumVoucher: number
   categories: WholeSaleCategory[]
   gaTrackingId?: string
   uaTrackingId?: string
   languages: string[]
   countries: ShopCountry[]
   shopCurrency: string
   brandHouseId: string
   productSearchEnabled?: boolean
   productSearchMask?: string
   hideLanguageSelector?: boolean
   defaultImage: string
}

export interface ShopCountry {
   code: string
   currency: string
   name: string
}

export const AppDataAtom = atom<AppData>({
   key: 'AppData',
   default: {
      initialized: false,
      languages: ['da', 'en'],
      countries: [],
      wholeSaleActive: false,
      maximumVoucher: 0,
      categories: [],
      localizations: [],
      shopCurrency: 'DKK',
      brandHouseId: '',
      defaultImage: 'https://cdn.create2stay.com/brandstatic/defaultimage7.png'
   }
});

export const ApplicationDataProvider: FC<any> = ({children}) => {
   const appContext = useRecoilValue(AccessContextData);
   const [appData, setAppData] = useRecoilState(AppDataAtom);
   const {data} = useAppInitTradeInAppQuery({
      variables: {
         shopId: appContext.shopId
      }
   })
   useEffect(() => {
      if (data) {
         setAppData({
            initialized: true,
            localizations: data.BrandLocalizations!.map(bl => {
               return {
                  key: bl!.key,
                  value: bl!.value,
                  language: bl!.language
               } as Localization
            }),
            languages: data.ShopLanguages!.map(bl => bl!),
            countries: data.ShopCountries!.map(country => ({
               code: country!.code,
               name: country!.name,
               currency: country!.currency
            })),
            categories: data.WholeSaleCategories.map(bc => {
               return {
                  _id: bc._id,
                  value: bc.value,
                  iconId: bc.iconId,
                  name: bc.name,
                  nameLocalized: bc.nameLocalized,
                  advanced: bc.advanced,
                  section: bc.section,
                  sectionLocalized: bc.sectionLocalized
               } as WholeSaleCategory
            }),
            maximumVoucher: data.TradeInConfig?.maximumVoucher || 0,
            wholeSaleActive: data.TradeInConfig?.wholeSaleActive || false,
            invalidEmailReg: data.TradeInConfig?.invalidEmailReg || undefined,
            brandHouseId: data.TradeInConfig!.brandHouseId!,
            gaTrackingId: data.GATrackingId!,
            uaTrackingId: data.UATrackingId!,
            shopCurrency: data.TradeInConfig?.shopCurrency || 'DKK',
            productSearchEnabled: data.TradeInConfig?.productSearchEnabled || false,
            productSearchMask: data.TradeInConfig?.productSearchMask || undefined,
            hideLanguageSelector: data.TradeInConfig?.hideLanguageSelector || false,
            defaultImage: data.TradeInConfig?.defaultImage || 'https://cdn.create2stay.com/brandstatic/defaultimage7.png'
         });

         Analytics.initialize(data.UATrackingId, data.GATrackingId);

         data.WholeSaleCategories.forEach(cat => {
            if (cat.iconId) {
               new Image().src = ImageServerUrl + '/' + cat.iconId
            }
         });

         new Image().src = Images.PLACEHOLDER
      }
   }, [data, setAppData])
   if (appData.initialized) {
      return children
   } else {
      return <></>
   }
}
